/**
 * Tide Run's rising sea (docs/TIDE_RUN.md): a band from the surface down past
 * the bottom of the screen, with a wavy top, foam and bubbles, tinted for the
 * world. Purely a view: TideRun.ts decides where the surface is.
 *
 * Reduced motion: the water still rises, because it is the game, but the
 * waves hold still and the warning glow doesn't pulse.
 */
import Phaser from 'phaser';
import { animalY } from '../../systems/tower';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PORTRAIT_DOCK_TOP } from '../../types';
import type { BackgroundTheme } from '../../types';

/** Over the animal (so a caught animal sits in it), under the HUD and dock. */
export const WATER_DEPTH = 24;
const GLOW_DEPTH = 29;

/** How far under the feet of an animal on the same platform the surface sits. */
const SURFACE_DROP = 4;
const WAVE_STEP = 20;
const WAVE_HEIGHT = 6;
const BUBBLES = 14;
const GLOW_HEIGHT = 130;
/**
 * The glow's own colour: the light aqua of the sea coming up. Coral was tried
 * first and vanished against the sunset sky the first ten levels are set in.
 */
const GLOW_COLOR = 0x7fe7ff;

interface WaterTint {
  readonly body: number;
  /** Foam and the lit top of the water. */
  readonly light: number;
}

const TINTS: Readonly<Record<BackgroundTheme, WaterTint>> = {
  sunset: { body: 0x2a4f7a, light: 0xffc98a },
  'midday-glacier': { body: 0x2d8bc9, light: 0xbfe9ff },
  'snowstorm-dusk': { body: 0x334a6b, light: 0xa9c1dd },
  'aurora-night': { body: 0x0f2a44, light: 0x5fe3c0 },
  'iceberg-sea': { body: 0x1d6fa5, light: 0x9fe0ff },
  'research-station': { body: 0x2b4a66, light: 0xc4d8e8 },
  'ice-cave': { body: 0x0f5e66, light: 0x5ff2e0 },
  blizzard: { body: 0x3d5875, light: 0xdbe8f5 },
  'midnight-sun': { body: 0x2f6aa0, light: 0xffe1a8 },
  'grand-aurora': { body: 0x14284a, light: 0x9d8cff },
};

interface Bubble {
  x: number;
  /** Below the surface, in px. */
  below: number;
  speed: number;
  radius: number;
}

export class Water {
  private readonly body: Phaser.GameObjects.Graphics;
  private readonly glow: Phaser.GameObjects.Graphics;
  private readonly tint: WaterTint;
  private readonly bubbles: Bubble[] = [];
  private lastAt = 0;
  private danger = false;

  constructor(
    private readonly scene: Phaser.Scene,
    theme: BackgroundTheme,
    private readonly reducedMotion: boolean,
  ) {
    this.tint = TINTS[theme];
    this.body = scene.add.graphics().setDepth(WATER_DEPTH);
    this.glow = scene.add.graphics().setScrollFactor(0).setDepth(GLOW_DEPTH).setAlpha(0);
    this.drawGlow();
    for (let i = 0; i < BUBBLES; i += 1) {
      this.bubbles.push({
        x: Math.random() * GAME_WIDTH,
        below: 30 + Math.random() * 260,
        speed: 18 + Math.random() * 30,
        radius: 2 + Math.random() * 4,
      });
    }
  }

  /** The surface's y in the world for a water position in platform units. */
  static surfaceY(position: number): number {
    return animalY(position) + SURFACE_DROP;
  }

  update(position: number, nowMs: number): void {
    const dt = this.lastAt === 0 ? 0 : Math.min(0.1, (nowMs - this.lastAt) / 1000);
    this.lastAt = nowMs;
    const camera = this.scene.cameras.main;
    const bottom = camera.scrollY + GAME_HEIGHT + 40;
    const surface = Water.surfaceY(position);

    const g = this.body;
    g.clear();
    if (surface > bottom) return;

    const t = this.reducedMotion ? 0 : nowMs / 1000;
    const wave = (x: number): number =>
      surface + Math.sin(x * 0.028 + t * 2.1) * WAVE_HEIGHT + Math.sin(x * 0.011 - t * 1.3) * 3;

    const points: Phaser.Math.Vector2[] = [];
    for (let x = 0; x <= GAME_WIDTH + WAVE_STEP; x += WAVE_STEP) {
      points.push(new Phaser.Math.Vector2(x, wave(x)));
    }

    g.fillStyle(this.tint.body, 0.82);
    g.beginPath();
    g.moveTo(0, bottom);
    for (const p of points) g.lineTo(p.x, p.y);
    g.lineTo(GAME_WIDTH + WAVE_STEP, bottom);
    g.closePath();
    g.fillPath();

    // A lighter band just under the surface, then the foam line on top.
    g.fillStyle(this.tint.light, 0.22);
    g.fillRect(0, surface + WAVE_HEIGHT, GAME_WIDTH, 18);
    g.lineStyle(4, 0xffffff, 0.75);
    g.strokePoints(points);
    g.lineStyle(2, this.tint.light, 0.9);
    g.strokePoints(points.map((p) => new Phaser.Math.Vector2(p.x, p.y + 5)));

    g.fillStyle(0xffffff, 0.35);
    for (const bubble of this.bubbles) {
      if (!this.reducedMotion) {
        bubble.below -= bubble.speed * dt;
        if (bubble.below < 10) {
          bubble.below = 120 + Math.random() * 220;
          bubble.x = Math.random() * GAME_WIDTH;
        }
      }
      const y = surface + bubble.below;
      if (y < bottom) g.fillCircle(bubble.x, y, bubble.radius);
    }
  }

  /** The near-water warning: a soft glow at the bottom of what the player sees. */
  setDanger(danger: boolean): void {
    if (danger === this.danger) return;
    this.danger = danger;
    this.scene.tweens.killTweensOf(this.glow);
    if (!danger) {
      this.scene.tweens.add({ targets: this.glow, alpha: 0, duration: 400 });
      return;
    }
    if (this.reducedMotion) {
      this.glow.setAlpha(0.8);
      return;
    }
    this.glow.setAlpha(0.5);
    this.scene.tweens.add({
      targets: this.glow,
      alpha: 1,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  destroy(): void {
    this.scene.tweens.killTweensOf(this.glow);
    this.body.destroy();
    this.glow.destroy();
  }

  /**
   * Wide stage: along the bottom edge. Tall stage: the device keyboard hides
   * the bottom, so the glow rises from the top of the word dock instead.
   */
  private drawGlow(): void {
    const base = IS_PORTRAIT ? PORTRAIT_DOCK_TOP : GAME_HEIGHT;
    const bands = 18;
    for (let i = 0; i < bands; i += 1) {
      const alpha = 0.75 * (1 - i / bands) ** 1.6;
      this.glow.fillStyle(GLOW_COLOR, alpha);
      const h = GLOW_HEIGHT / bands;
      this.glow.fillRect(0, base - (i + 1) * h, GAME_WIDTH, h);
    }
  }
}
